import type {
  MediaConstraints,
  MediaStreamLike,
  PeerConfiguration,
  PeerConnectionLike,
} from './calls/webrtc';

/**
 * The handful of things a browser and a phone genuinely disagree about.
 *
 * Everything else in this package is arithmetic on cached data and works the
 * same in both. These two do not: there is no `localStorage` in React Native
 * and no `document.hidden` either, and pretending otherwise would either crash
 * the app or silently take the wrong branch.
 *
 * Both have a working default, so a client that never configures them behaves
 * sensibly — a preference simply does not survive a restart, and the app is
 * assumed to be visible.
 */
export interface KeyValueStore {
  get(key: string): string | null;
  set(key: string, value: string): void;
}

class MemoryKeyValueStore implements KeyValueStore {
  private readonly values = new Map<string, string>();

  get(key: string): string | null {
    return this.values.get(key) ?? null;
  }

  set(key: string, value: string): void {
    this.values.set(key, value);
  }
}

let store: KeyValueStore = new MemoryKeyValueStore();
let visible: () => boolean = () => true;

/**
 * Small, non-secret preferences: which device this is, whether the details
 * panel was open. Credentials never come through here — they go to the
 * platform keystore, which is a different thing with different guarantees.
 */
export function provideKeyValueStore(next: KeyValueStore): void {
  store = next;
}

export function preference(): KeyValueStore {
  return store;
}

/**
 * Whether the interface is actually in front of the person.
 *
 * Marking a message read means telling the sender it was seen, so it must be
 * true: a conversation open in a backgrounded tab or an app in the recents
 * list has not been read by anyone.
 */
export function provideVisibility(next: () => boolean): void {
  visible = next;
}

export function isVisible(): boolean {
  return visible();
}

let release: (previewUrl: string) => void = () => undefined;

/**
 * Let go of a local preview.
 *
 * A browser object URL pins the whole file in memory until it is revoked. A
 * React Native file URI pins nothing, so the default does nothing — which is
 * correct, not a stub.
 */
export function provideReleasePreview(next: (previewUrl: string) => void): void {
  release = next;
}

export function releasePreview(previewUrl: string): void {
  release(previewUrl);
}

/**
 * The WebRTC primitives, which the two platforms spell the same way but do not
 * share.
 *
 * The browser has them as globals. React Native has none — `react-native-webrtc`
 * supplies an API shaped like the browser's, backed by the same native library
 * Chrome uses. Everything about a call other than these two calls is identical
 * on both, so only these are injected.
 */
export interface WebRtcSupport {
  createPeerConnection(configuration: PeerConfiguration): PeerConnectionLike;
  getUserMedia(constraints: MediaConstraints): Promise<MediaStreamLike>;
  /** True when the failure was the person declining the microphone or camera. */
  isPermissionDenied(error: unknown): boolean;
}

let webrtc: WebRtcSupport | null = null;

export function provideWebRtc(support: WebRtcSupport): void {
  webrtc = support;
}

/** False on a build with no WebRTC at all; the interface then hides calling. */
export function callsAvailable(): boolean {
  return webrtc !== null;
}

export function webRtc(): WebRtcSupport {
  if (!webrtc) {
    throw new Error('@liniya/client-core: звонки не настроены — нужен provideWebRtc().');
  }
  return webrtc;
}

/**
 * Register what should happen when the client is about to disappear — a tab
 * closing, an app being killed. Without it the other side rings until it times
 * out, with nobody there.
 */
let registerClosing: (handler: () => void) => () => void = () => () => undefined;

export function provideAppClosing(register: (handler: () => void) => () => void): void {
  registerClosing = register;
}

export function onAppClosing(handler: () => void): () => void {
  return registerClosing(handler);
}
